/**
 * Providers, the demo dock, and the router outlet.
 *
 * The direction and theme preferences are written to the html element, which
 * is the entire switching mechanism: the token layer keys off those two
 * attributes, so a brand direction changes on a live screen with no rebuild
 * and no component edit.
 */

import { useEffect } from 'react';
import { BrowserRouter } from 'react-router';
import { DemoDock } from './components/demo/DemoDock';
import { DocumentTitle } from './components/shell/DocumentTitle';
import { ErrorBoundary } from './components/shell/ErrorBoundary';
import { setLang } from './lib/i18n/t';
import { useAuthInit } from './lib/auth/use-auth-init';
import { useAccountLanguageSync } from './lib/hooks/use-language';
import { useDemoStore } from './lib/mock/store';
import { PostHogProvider } from './lib/analytics/PostHogProvider';
import { AppRoutes } from './routes';

// Real production build: no demo apparatus, no floating chip, no D shortcut.
// VITE_API_MODE=mock (the default in .env.local) keeps every bit of it:
// this is the only gate, nothing under components/demo or lib/mock is touched.
const USE_MOCK = import.meta.env.VITE_API_MODE === 'mock';

export default function App() {
  useAuthInit();
  // The account's saved language wins over this browser's, once it loads.
  useAccountLanguageSync();
  const direction = useDemoStore((s) => s.prefs.direction);
  const theme = useDemoStore((s) => s.prefs.theme);
  const lang = useDemoStore((s) => s.prefs.lang);

  // Set before children render, so the first paint is already in the language.
  setLang(lang);

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-direction', direction);
    root.setAttribute('data-theme', theme);
    root.setAttribute('lang', lang === 'th' ? 'th' : 'en');
  }, [direction, theme, lang]);

  return (
    <BrowserRouter>
      <PostHogProvider>
        <ErrorBoundary>
          {/* Keyed on language so a switch re-renders every string in one pass.
              The tab title is inside the key for the same reason: it is a string
              in the catalogue like any other. */}
          <div style={{ display: 'contents' }} key={lang}>
            <DocumentTitle />
            <AppRoutes />
          </div>
          {USE_MOCK ? <DemoDock /> : null}
        </ErrorBoundary>
      </PostHogProvider>
    </BrowserRouter>
  );
}
